import { Skeleton, width, mt } from '@openeducation/oe-it-components';
import { teacherCurrentLiveClassAtom } from '@openeducation/oe-it-jotai-atoms';
import { useNotificationContext } from '@openeducation/oe-it-providers';
import type { LiveClassTeacherReminderNotificationType } from '@openeducation/oe-it-shared-types';
import { styled, useDimension } from '@openeducation/oe-it-themes';
import { useSetAtom } from 'jotai';
import React, { useMemo } from 'react';
import { View } from 'react-native';

import { CountdownContainer } from './countdown-container';
import { Card } from '../../atoms/card/card';
import { InfoItem } from '../../atoms/info-item/info-item';
import { useLiveClass } from '../../hooks/use-live-class';
import { useRejoinClasses } from '../../hooks/use-rejoin-classes';
import { useTimer } from '../../hooks/use-timer';
import { useWorkSchedule } from '../../hooks/use-work-schedule';
import { RejoinBanner } from '../rejoin-banner/rejoin-banner';

const notificationTitle = {
  SetupTimeStartNotification: 'Setup time remaining',
  TeachingTimeStartNotification: 'Teaching time remaining',
  FeedbackTimeStartNotification: 'Feedback time remaining',
  EmptyClassNotification: '',
};

export interface WorkScheduleCardProps {}

export const WorkScheduleCard = () => {
  const { isTablet } = useDimension();
  const { getTimeDelta } = useTimer();
  const { isLoadingNextLiveClasses, nextLiveClasses, msToNextClassBegin } =
    useLiveClass();
  const { workSchedules, isLoadingWorkSchedule } = useWorkSchedule();
  const {
    lcTeacherReminderNotifications,
    isLoadingLCTeacherReminderNotifications,
  } = useNotificationContext();

  const [workSchedule] = workSchedules || [];

  const hasNextLiveClass = !!nextLiveClasses?.length;
  const isLoadingAll =
    isLoadingWorkSchedule &&
    isLoadingNextLiveClasses &&
    isLoadingLCTeacherReminderNotifications;

  const { title, counterMs } = useMemo(() => {
    if (!hasNextLiveClass) {
      return {
        title: 'No live classes scheduled',
      };
    }

    const findNotification = (type: LiveClassTeacherReminderNotificationType) =>
      lcTeacherReminderNotifications?.find(
        (notification) => notification.notification === type
      );

    const notification =
      findNotification('SetupTimeStartNotification') ||
      findNotification('TeachingTimeStartNotification') ||
      findNotification('FeedbackTimeStartNotification');

    if (notification?.notification && notification?.expirationMs) {
      return {
        title: notificationTitle[notification.notification],
        counterMs: getTimeDelta(notification.expirationMs),
      };
    }

    return {
      title: 'Time until next live class',
      counterMs: getTimeDelta(msToNextClassBegin),
    };
  }, [
    hasNextLiveClass,
    msToNextClassBegin,
    getTimeDelta,
    lcTeacherReminderNotifications,
  ]);

  const setCurrentLiveClassLessonAtom = useSetAtom(teacherCurrentLiveClassAtom);
  const { rejoinClasses } = useRejoinClasses();
  React.useEffect(() => {
    if (rejoinClasses && rejoinClasses?.length > 0) {
      const id = rejoinClasses?.[0]?.id;
      if (id) {
        setCurrentLiveClassLessonAtom(id);
      }
    } else {
      setCurrentLiveClassLessonAtom(undefined);
    }
  }, [rejoinClasses, setCurrentLiveClassLessonAtom]);

  return (
    <Card style={width(295)}>
      {rejoinClasses?.map((item) => (
        <RejoinBanner key={item.id} rejoinClass={item} />
      ))}
      {isLoadingAll ? (
        <InnerContainer isTablet={isTablet}>
          <View>
            <Skeleton width={232} height={18} />
            <Skeleton width={232} height={28} style={mt(4)} />
          </View>

          <View>
            <Skeleton width={232} height={18} />
            <Skeleton width={232} height={86} style={mt(4)} />
          </View>
        </InnerContainer>
      ) : (
        <InnerContainer isTablet={isTablet}>
          <InfoItem
            title="Work schedule"
            text={workSchedule?.formattedSchedule ?? 'Not scheduled to work!'}
            textType="primary"
            textSize={isTablet ? 'large' : 'regular'}
            titleType="dark"
          />

          <InfoItem title={title} titleType="dark">
            {hasNextLiveClass && (
              <CountdownContainer msToNextClassBegin={counterMs} />
            )}
          </InfoItem>
        </InnerContainer>
      )}
    </Card>
  );
};

interface InnerContainerProps {
  isTablet: boolean;
}

const InnerContainer = styled.View<InnerContainerProps>`
  flex-direction: ${({ isTablet }) => (isTablet ? 'row' : 'column')};
  justify-content: space-around;
  flex-wrap: wrap;
  gap: 16px;
`;
